import { merchantKeys, type Merchant } from '@taler/api-client'
import { useInfiniteQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { keepPreviousListResult, sameLedger } from '@/app/keep-previous-list'

export type { Merchant }

export const MERCHANTS_PAGE_SIZE = 25
const SEARCH_PARAM = 'q'

export interface UseMerchantsOptions {
  readonly ledgerId: () => string | undefined
  /** The screen never issues a request for a capability the ledger did not grant. */
  readonly enabled: () => boolean
}

export interface UseMerchants {
  readonly merchants: ComputedRef<readonly Merchant[]>
  readonly search: ComputedRef<string>
  readonly isLoading: ComputedRef<boolean>
  readonly isFetchingNextPage: ComputedRef<boolean>
  readonly hasNextPage: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  setSearch(value: string): void
  fetchNextPage(): void
  retry(): void
}

function firstValue(raw: unknown): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' && value !== '' ? value : undefined
}

export function useMerchants(options: UseMerchantsOptions): UseMerchants {
  const api = useApi()
  const route = useRoute()
  const router = useRouter()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)
  const search = computed(() => firstValue(route.query[SEARCH_PARAM]) ?? '')

  const merchantsQuery = useInfiniteQuery({
    queryKey: computed(() =>
      merchantKeys.list(options.ledgerId() ?? '', {
        limit: MERCHANTS_PAGE_SIZE,
        ...(search.value === '' ? {} : { search: search.value }),
      }),
    ),
    placeholderData: computed(() => keepPreviousListResult(sameLedger(options.ledgerId()))),
    queryFn: ({ pageParam, signal }) =>
      api.merchants.list(
        options.ledgerId()!,
        {
          limit: MERCHANTS_PAGE_SIZE,
          ...(search.value === '' ? {} : { search: search.value }),
          ...(pageParam === undefined ? {} : { cursor: pageParam as string }),
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: active,
    retry: false,
  })

  return {
    merchants: computed(() => (merchantsQuery.data.value?.pages ?? []).flatMap((page) => page.items)),
    search,
    isLoading: computed(() => active.value && merchantsQuery.isPending.value),
    isFetchingNextPage: computed(() => merchantsQuery.isFetchingNextPage.value),
    hasNextPage: computed(() => merchantsQuery.hasNextPage.value),
    error: computed(() => merchantsQuery.error.value),
    retrying: retrying(merchantsQuery),
    setSearch: (value) => {
      const patch: Record<string, string | undefined> = { [SEARCH_PARAM]: value.trim() || undefined }
      const next = { ...route.query, ...patch }
      for (const key of Object.keys(next)) {
        if (next[key] === undefined) delete next[key]
      }
      void router.replace({ query: next })
    },
    fetchNextPage: () => {
      void merchantsQuery.fetchNextPage()
    },
    retry: () => {
      void merchantsQuery.refetch()
    },
  }
}
